/**
 * The weekly chest celebration (specs/home-page AC-23): shown once over Home when the chest was
 * granted and not yet acknowledged. Dismissing acknowledges it.
 */

import React, { useMemo } from 'react';
import { Modal, View, Text, Pressable, Platform } from 'react-native';
import { Image } from 'expo-image';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { useTheme } from '../../../src/theme/useTheme';
import { ASSET_PATHS, getAssetUrl } from '../../../constants/assets';
import { HOME_COPY, HOME_TEST_IDS } from '../../../src/home/constants/home.constants';
import { getChestCelebrationModalStyles } from './ChestCelebrationModal.styles';

const CHEST_ART_SOURCE = { uri: getAssetUrl(ASSET_PATHS.REWARD_ICON) };

export interface ChestCelebrationModalProps {
  visible: boolean;
  rewardCoins: number;
  onDismiss: () => void;
}

export const ChestCelebrationModal: React.FC<ChestCelebrationModalProps> = ({ visible, rewardCoins, onDismiss }) => {
  const theme = useTheme();
  const styles = useMemo(() => getChestCelebrationModalStyles(theme), [theme]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onDismiss}>
      <View style={styles.backdrop} testID={HOME_TEST_IDS.chestModal}>
        <View style={styles.card} accessibilityRole="alert">
          <Image source={CHEST_ART_SOURCE} style={styles.art} contentFit="contain" alt={WEB_IMAGE_ALT_DECORATIVE} />
          <Text style={styles.title}>{HOME_COPY.CHEST_TITLE}</Text>
          <Text style={styles.coins}>{HOME_COPY.coinsPlus(rewardCoins)}</Text>
          <Text style={styles.subtitle}>{HOME_COPY.CHEST_SUBTITLE}</Text>
          <Pressable
            style={({ pressed, hovered }) => [
              styles.button,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.buttonPressed,
              hovered && Platform.OS === 'web' && styles.buttonHover,
            ]}
            onPress={onDismiss}
            accessibilityRole="button"
            accessibilityLabel={HOME_COPY.CHEST_COLLECT}
            testID={HOME_TEST_IDS.chestModalDismiss}
          >
            <Text style={styles.buttonText}>{HOME_COPY.CHEST_COLLECT}</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
};
